import { Fragment } from "react";
import { Link } from "react-router-dom";
import { Faq } from "../components/Faq";
import type { AppConfig } from "../lib/api";

const PLATFORMS = [
  "Spotify",
  "Apple Music",
  "YouTube",
  "Instagram",
  "TikTok",
  "Amazon Music",
  "Deezer",
];

/** Deterministic bar heights (%) for the decorative hero waveform — no
 * randomness so the markup is stable across renders/tests. */
const WAVEFORM_HEIGHTS = [
  22, 38, 58, 80, 46, 92, 64, 30, 72, 50, 86, 40, 60, 96, 54, 34, 78, 44, 68, 26, 90, 48, 62,
  36, 82, 56, 20, 74, 42, 66,
];

function WaveIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 18 18" fill="none" aria-hidden="true">
      <rect x="1" y="6" width="2.4" height="6" rx="1.2" fill="currentColor" />
      <rect x="6" y="2" width="2.4" height="14" rx="1.2" fill="currentColor" />
      <rect x="11" y="5" width="2.4" height="8" rx="1.2" fill="currentColor" />
    </svg>
  );
}

function HeroVisual() {
  return (
    <div className="hero-visual" aria-hidden="true">
      <div className="hero-orb" />
      <div className="hero-rings" />
      <div className="hero-waveform">
        {WAVEFORM_HEIGHTS.map((h, i) => (
          <span key={i} style={{ height: `${h}%` }} />
        ))}
      </div>
    </div>
  );
}

const DELIVERABLES = [
  {
    title: "Campaign Identity",
    desc: "Concept, emotional angle, target listener, and a clear positioning statement.",
  },
  {
    title: "Song Descriptions",
    desc: "Short, medium, and ~500-character versions ready to paste anywhere.",
  },
  { title: "TikTok Kit", desc: "10 hooks, 5 captions, and 10 short-video concepts." },
  { title: "Instagram Kit", desc: "5 captions, a release-day post, and story ideas." },
  {
    title: "YouTube Copy",
    desc: "Video description, Shorts description, titles, and a pinned comment.",
  },
  {
    title: "Facebook & X/Threads",
    desc: "Ready-to-post copy across every remaining platform.",
  },
  {
    title: "Spotify Pitch",
    desc: "Editorial-style pitch copy and artist-profile promotional text.",
  },
  {
    title: "Discovery Tags",
    desc: "Genre, mood, and keyword tags built for search and discovery.",
  },
  {
    title: "Lyric-Video Concepts",
    desc: "5+ fully art-directed concepts with scene, lighting, and camera direction.",
  },
  {
    title: "Visual Prompts",
    desc: "Image and animation prompts for whatever generator you use.",
  },
  { title: "Press Copy", desc: "A short blurb and an extended press description." },
  {
    title: "30-Day Calendar",
    desc: "A realistic pre-release, release-day, and post-release plan.",
  },
];

const STEPS = [
  {
    title: "Tell us about your song",
    desc: "Artist name, title, genre, description, and anything else that helps — lyrics, mood, influences.",
  },
  {
    title: "Complete checkout",
    desc: "One secure payment through Stripe. No account, no password.",
  },
  {
    title: "AI builds your campaign",
    desc: "Your campaign is generated automatically and validated before delivery.",
  },
  {
    title: "Receive the complete package",
    desc: "View it on a secure page, download it, and it's emailed to you too.",
  },
];

const AUDIENCE = [
  "Independent artists",
  "Bands",
  "Producers",
  "Singer-songwriters",
  "DIY musicians",
  "Small labels",
];

const FAQ_ITEMS = [
  {
    question: "How does the generation actually work?",
    answer:
      "You tell us about your song — title, genre, description, and optionally lyrics and notes. Our system sends that information to an AI model with a detailed set of instructions and validates the structured result before it's ever delivered to you.",
  },
  {
    question: "How is it delivered?",
    answer:
      "To a secure order page tied to a private link, and by email once it's ready. You can view it in the browser or download it as Markdown, plain text, or an HTML file.",
  },
  {
    question: "How long does it take?",
    answer:
      "Most campaigns finish within a few minutes of payment. You don't need to keep the browser open — check back anytime with your order link.",
  },
  {
    question: "Do I need to create an account?",
    answer:
      "No. There are no accounts or passwords. Your order link and email are what you use to access your campaign.",
  },
  {
    question: "What information do you need from me?",
    answer:
      "At minimum: artist name, song title, genre, a short description of the song, and your email. Lyrics, mood, influences, and other details are optional but help the campaign feel specific to your song.",
  },
  {
    question: "Does ReleaseMachine listen to my song?",
    answer:
      "No — this version does not process audio. The campaign is generated entirely from the text information you provide, so more detail generally produces a more specific result.",
  },
  {
    question: "Will this get me on Spotify editorial playlists?",
    answer:
      "No service can guarantee editorial placement, and we don't claim to. The Spotify section gives you strong pitch copy to use when submitting through Spotify for Artists — the decision is always theirs.",
  },
  {
    question: "What's your refund policy?",
    answer:
      "Because this is an automatically generated digital product delivered right after payment, refunds are handled case by case — see our Refund Policy for details. If generation fails outright, you're covered.",
  },
  {
    question: "How is my data handled?",
    answer:
      "Your song information is used only to generate and deliver your campaign, and is processed by the infrastructure and AI providers necessary to do that. See our Privacy Policy for the full picture.",
  },
  {
    question: "Does ReleaseMachine post anything on my behalf?",
    answer:
      "No. We never post to any of your social accounts. You receive ready-to-use copy and you decide when and where to post it.",
  },
];

export function Landing({ config }: { config: AppConfig | null }) {
  const priceDisplay = ((config?.displayPriceCents ?? 999) / 100).toFixed(2);

  return (
    <>
      <section className="hero">
        <div className="container">
          <div className="hero-copy">
            <span className="eyebrow">AI Release Marketing, Automated</span>
            <h1>Your song is finished. Your campaign isn&rsquo;t.</h1>
            <p className="lede">
              ReleaseMachine turns the information about your song into a complete,
              ready-to-use marketing campaign — automatically, in minutes, for a single
              one-time payment.
            </p>
            <div className="hero-actions">
              <Link to="/build" className="btn btn-primary">
                Build My Release Campaign
              </Link>
              <Link to="/#how-it-works" className="hero-play">
                <span className="hero-play-icon" aria-hidden="true">
                  ▶
                </span>
                See how it works
              </Link>
            </div>
            <p className="hero-meta">
              <span>${priceDisplay} one time</span>
              <span>No subscription</span>
              <span>No account required</span>
            </p>
          </div>
          <HeroVisual />
        </div>
      </section>

      <div className="trust-bar">
        <div className="container">
          <span className="eyebrow">Promote your release across</span>
          <div className="trust-logos">
            {PLATFORMS.map((p) => (
              <span key={p}>{p}</span>
            ))}
          </div>
        </div>
      </div>

      <section className="section" aria-labelledby="what-you-get">
        <div className="container">
          <div className="section-head">
            <span className="eyebrow">What you get</span>
            <h2 id="what-you-get">One payment. A complete campaign.</h2>
            <p>
              Every order includes a full platform-by-platform marketing package built
              around your song.
            </p>
          </div>
          <div className="grid grid-3">
            {DELIVERABLES.map((d) => (
              <div className="card" key={d.title}>
                <span className="feature-icon">
                  <WaveIcon />
                </span>
                <h3 style={{ fontSize: "1rem", marginBottom: 8 }}>{d.title}</h3>
                <p style={{ color: "var(--text-muted)", fontSize: "0.9rem" }}>{d.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="section" id="how-it-works" aria-labelledby="how-it-works-head">
        <div className="container">
          <div className="section-head">
            <span className="eyebrow">How it works</span>
            <h2 id="how-it-works-head">From song to campaign in four steps</h2>
          </div>
          <div className="steps-row">
            {STEPS.map((s, idx) => (
              <Fragment key={s.title}>
                {idx > 0 && (
                  <span className="step-connector" aria-hidden="true">
                    →
                  </span>
                )}
                <div className="card step-card">
                  <span className="step-number">{idx + 1}</span>
                  <h3 style={{ fontSize: "1rem", marginBottom: 8 }}>{s.title}</h3>
                  <p style={{ color: "var(--text-muted)", fontSize: "0.9rem" }}>{s.desc}</p>
                </div>
              </Fragment>
            ))}
          </div>
        </div>
      </section>

      <section className="section-tight" aria-labelledby="who-for">
        <div className="container">
          <div className="section-head">
            <span className="eyebrow">Who it&rsquo;s for</span>
            <h2 id="who-for">Built for people who release music themselves</h2>
          </div>
          <div className="grid grid-3">
            {AUDIENCE.map((a) => (
              <div className="card" key={a} style={{ textAlign: "center" }}>
                <p style={{ fontWeight: 600 }}>{a}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="section" id="pricing" aria-labelledby="pricing-head">
        <div className="container">
          <div className="section-head">
            <span className="eyebrow">Pricing</span>
            <h2 id="pricing-head">Simple, one-time pricing</h2>
          </div>
          <div className="pricing-split">
            <div className="card price-card">
              <span className="eyebrow">Complete Release Pack</span>
              <div className="price-amount">
                ${priceDisplay} <span>one time</span>
              </div>
              <p style={{ color: "var(--text-muted)", fontSize: "0.9rem" }}>
                No subscription. No hidden fees.
              </p>
              <ul className="price-list">
                <li>No account required</li>
                <li>Secure one-time payment via Stripe</li>
                <li>Full campaign delivered in minutes</li>
              </ul>
              <Link to="/build" className="btn btn-primary btn-block">
                Build My Release Campaign
              </Link>
            </div>
            <div className="card price-detail-card">
              <ul className="price-checklist">
                <li>Full platform-by-platform marketing copy</li>
                <li>30-day release calendar</li>
                <li>Lyric-video &amp; visual prompts</li>
                <li>TikTok, Instagram &amp; YouTube kits</li>
                <li>Delivered by secure link &amp; email</li>
                <li>Markdown, text &amp; HTML downloads</li>
              </ul>
              <div className="price-badge-circle">
                <span className="amount">${priceDisplay}</span>
                <span className="label">One-time</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="section" id="faq" aria-labelledby="faq-head">
        <div className="container" style={{ maxWidth: 900 }}>
          <div className="section-head">
            <span className="eyebrow">FAQ</span>
            <h2 id="faq-head">Questions, answered</h2>
          </div>
          <div className="faq-columns">
            <Faq items={FAQ_ITEMS} />
          </div>
        </div>
      </section>
    </>
  );
}
